import React, { Component, useMemo, useState, useRef } from 'react'
import type { ReactNode, ErrorInfo } from 'react'
import { throttle } from 'lodash'
import * as Sentry from '@sentry/browser'
import {
  Icon,
  Button,
  Modal,
  Header,
  Message,
  ApplicationModalsProvider,
  ApplicationNotificationsProvider,
  ApplicationThemeProvider,
  useApplicationThemeRaw,
} from '@adc/ui-core'
import { ProductLine } from '@adc/constants'
import type { Theme } from '@adc/ui-core'
import logger from '@adc/logger'
import configment from '@adc/configment'
import { useMountEffect } from '../../hooks/lifecycle'
import { ApplicationContextProvider } from './context'

const NetworkStatusMonitor = () => {
  const [isOffline, toggleIsOffline] = useState(false)
  const setOffline = throttle(() => toggleIsOffline(true), 250)
  const setOnline = throttle(() => toggleIsOffline(false), 250)

  useMountEffect(() => {
    window.addEventListener('offline', setOffline)
    window.addEventListener('online', setOnline)

    return () => {
      window.removeEventListener('offline', setOffline)
      window.removeEventListener('online', setOnline)
    }
  })

  // We aren't doing anything interesting now, but if we chose to do so, this would be where we show
  // a toast notification saying the network is offline or whatnot.
  if (isOffline) {
    logger.warn('Network is offline')
  }

  // TODO: Perhaps have a modal or small notification showing this? Until then, we track it, but no UI impact
  return null
}

interface ErrorModalProps {
  contactEmail?: string
  error: Error & { componentStack?: string }
}

const ErrorModal = ({ contactEmail, error }: ErrorModalProps) => {
  const trace = error.componentStack
    ? [error.message, error.componentStack].join(' ')
    : error.stack

  return (
    <Modal size={'medium' as any} open closeIcon={false}>
      <Header>
        <Icon name='warning sign' size='big' color='error' />
        <Header.Content>Something went wrong</Header.Content>
      </Header>
      <Modal.Content>
        <div className='u-mb-3'>
          Please try again or contact{' '}
          <a href={`mailto:${contactEmail}`}>{contactEmail}</a>.
        </div>

        <div className='u-mb-3'>
          Here is the error. Feel free to screenshot and send it Application
          Support. We already capture this information, so we should have this
          already, but it's always good to double check with what you are
          seeing.
        </div>

        <pre>
          <Message negative>{trace}</Message>
        </pre>
      </Modal.Content>
      <Modal.Actions>
        <Button
          role='primary'
          size='medium'
          id='error_reload_page'
          elmId='error_reload_page'
          onClick={() => location.reload()}
        >
          Reload the page
        </Button>
      </Modal.Actions>
    </Modal>
  )
}

interface ApplicationErrorBoundaryProps {
  children: ReactNode
}

interface ApplicationErrorBoundaryState {
  errorCaught: boolean
  errorRef: (Error & { componentStack?: string }) | null
}

class ApplicationErrorBoundary extends Component<
  ApplicationErrorBoundaryProps,
  ApplicationErrorBoundaryState
> {
  static getDerivedStateFromError(
    error: Error
  ): Partial<ApplicationErrorBoundaryState> {
    return {
      errorCaught: true,
      errorRef: error,
    }
  }

  state: ApplicationErrorBoundaryState = {
    errorCaught: false,
    errorRef: null,
  }

  componentDidCatch(
    error: Error & { componentStack?: string },
    errorInfo: ErrorInfo
  ) {
    logger.error(error.message, errorInfo)

    error.componentStack = errorInfo.componentStack ?? undefined
    this.setState({ errorRef: error })

    if (error instanceof Error) {
      Sentry.captureException(error)
    }
  }

  render() {
    return this.state.errorCaught ? (
      <ErrorModal error={this.state.errorRef!} />
    ) : (
      this.props.children
    )
  }
}

const DEFAULT_THEME: Theme = {
  productLine: ProductLine.Morpheus,
  version: 1,
}

interface MorpheusApplicationShellProps {
  children: ReactNode
  /**
   * @deprecated Modal rendering is no longer gated by capabilities. This prop is
   * accepted for backward compatibility but ignored, and will be removed in a
   * future major version.
   */
  capabilities?: Record<string, any>
  overridableComponents?: Record<string, ReactNode>
  sspConfig?: Record<string, any>
  theme?: Theme
  // Global user session. Passed from the Redux-connected container so the
  // application context stays reactive to login/logout. The shell renders a
  // nested ApplicationContextProvider that inherits graphWebsocket from the
  // top-level provider (in the app's client/index.js) while owning user.
  user?: Record<string, unknown>
}

const MorpheusApplicationShell = ({
  children,
  // `capabilities` is intentionally not destructured: modal rendering is no
  // longer gated by it. The prop remains (deprecated) so callers don't break.
  overridableComponents = {},
  sspConfig = {},
  theme: themeInjected,
  user,
}: MorpheusApplicationShellProps) => {
  // This is a shared well-known trigger for our integration tests to wait before testing.
  useMountEffect(() => {
    document.body.setAttribute('data-app-state', 'ready')
  })

  const themeFromContext = useApplicationThemeRaw()
  const theme = useMemo(() => {
    let newTheme = DEFAULT_THEME

    if (themeInjected) {
      newTheme = themeInjected
    } else if (themeFromContext) {
      newTheme = themeFromContext
    } else {
      const themeFromConfig =
        sspConfig?.APP_THEME ||
        configment.get('APP_THEME', ProductLine.Morpheus)

      const versionFromConfig =
        Number(
          sspConfig?.APP_THEME_VERSION || configment.get('APP_THEME_VERSION', 1)
        ) || 1

      if (themeFromConfig) {
        if (Object.values(ProductLine).includes(themeFromConfig)) {
          newTheme = {
            productLine: themeFromConfig,
            version: versionFromConfig,
          }
        }
      }
    }
    logger.info('Theme:', newTheme.productLine)
    return newTheme
  }, [
    sspConfig?.APP_THEME,
    sspConfig?.APP_THEME_VERSION,
    themeFromContext,
    themeInjected,
  ])

  // used to hand off element where we want to render page banner
  const pageBannerContainerRef = useRef(null)

  return (
    <ApplicationContextProvider user={user}>
      <ApplicationThemeProvider theme={theme}>
        <ApplicationErrorBoundary>
          <ApplicationNotificationsProvider
            pageBannerContainerRef={pageBannerContainerRef}
          >
            <ApplicationModalsProvider>
              {overridableComponents.header}
              <div ref={pageBannerContainerRef} />
              {children}
              {overridableComponents.footer}
              <NetworkStatusMonitor />
            </ApplicationModalsProvider>
          </ApplicationNotificationsProvider>
        </ApplicationErrorBoundary>
      </ApplicationThemeProvider>
    </ApplicationContextProvider>
  )
}

export default MorpheusApplicationShell
